/**
 * UnifiedSelectionFooter - Generic selection footer for UnifiedDataGrid
 *
 * Shows selection count and actions (delete, etc.) when rows are selected.
 * Works with any EntityConfig.
 */

import React, { useCallback } from 'react'
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import ClearIcon from '@mui/icons-material/Clear'
import { GridFooterContainer, GridPagination, GridRowId, GridRowSelectionModel, GridApiPremium } from '@mui/x-data-grid-premium'
import { EntityConfig } from '../types/EntityConfig'

interface UnifiedSelectionFooterProps {
  /** Entity configuration */
  config: EntityConfig
  /** Current selection model */
  rowSelectionModel: GridRowSelectionModel
  /** Set selection model function */
  setRowSelectionModel: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>
  /** Grid API ref */
  apiRef: React.RefObject<GridApiPremium>
  /** Called when delete button is clicked */
  onDeleteClick?: (selectedIds: Set<GridRowId>) => void
  /** Label for items (overrides config.labels.plural) */
  itemLabel?: string
  /** Whether server-side pagination is enabled */
  serverSideMode?: boolean
  /** Total row count for display */
  totalRowCount?: number
  /** Filtered row count for display */
  filterRowCount?: number
}

export const UnifiedSelectionFooter: React.FC<UnifiedSelectionFooterProps> = ({
  config,
  rowSelectionModel,
  setRowSelectionModel,
  apiRef,
  onDeleteClick,
  itemLabel,
  serverSideMode = false,
  totalRowCount,
  filterRowCount,
}) => {
  const selectionCount = rowSelectionModel?.ids?.size ?? 0
  const hasSelection = selectionCount > 0

  const label = itemLabel || config.labels.plural
  const singularLabel = label.endsWith('s') ? label.slice(0, -1) : label
  const displayLabel = selectionCount === 1 ? singularLabel : label

  const clearSelection = useCallback(() => {
    try {
      const ids = rowSelectionModel?.ids
      if (ids && typeof ids.forEach === 'function') {
        ids.forEach((id: GridRowId) => apiRef.current?.selectRow(id, false))
      }
      setRowSelectionModel({ ids: new Set(), type: 'include' })
    } catch {
      setRowSelectionModel({ ids: new Set(), type: 'include' })
    }
  }, [apiRef, rowSelectionModel, setRowSelectionModel])

  const handleDeleteClick = useCallback(() => {
    if (onDeleteClick) {
      onDeleteClick(rowSelectionModel.ids)
    }
  }, [onDeleteClick, rowSelectionModel])

  return (
    <GridFooterContainer
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 52,
        overflow: 'hidden',
        px: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      {hasSelection ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 0.5,
            px: 2,
            borderRadius: 2,
            backgroundColor: 'action.selected',
          }}
        >
          {/* Selection count */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={selectionCount}
              size="small"
              color="primary"
              sx={{ fontWeight: 'bold' }}
            />
            <Typography variant="body2" noWrap>
              {displayLabel} selected
            </Typography>
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Delete button */}
            {onDeleteClick && (
              <Tooltip
                title={selectionCount === 1 ? `Delete ${singularLabel}` : `Delete ${selectionCount} ${label}`}
                placement="top"
              >
                <IconButton onClick={handleDeleteClick} color="error" size="small">
                  <DeleteForeverIcon sx={{ height: 20, width: 20 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Clear selection button */}
            <Tooltip title="Clear selection" placement="top">
              <IconButton onClick={clearSelection} size="small">
                <ClearIcon sx={{ height: 20, width: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ) : (
        <>
          {/* Left side: Row counts when no selection */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {totalRowCount !== undefined && (
              <Typography variant="body2" color="text.secondary">
                {filterRowCount !== undefined && filterRowCount !== totalRowCount
                  ? `${filterRowCount} of ${totalRowCount} ${label}`
                  : `${totalRowCount} ${label}`}
              </Typography>
            )}
          </Box>

          {/* Right side: Pagination */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {serverSideMode && <GridPagination />}
          </Box>
        </>
      )}
    </GridFooterContainer>
  )
}

export default UnifiedSelectionFooter
